import React, { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import { RecipientInput } from './RecipientInput';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const err = (e) => toast({ title: 'Error', description: e?.response?.data?.detail || 'Request failed', variant: 'destructive' });

const Formats = ({ value, onChange, prefix, disabled }) => (
  <div className="flex gap-4">
    {[['pdf', 'PDF'], ['xlsx', 'Excel']].map(([k, l]) => (
      <label key={k} className="flex items-center gap-1.5 text-sm">
        <Checkbox disabled={disabled} checked={value.includes(k)} onCheckedChange={(c) => onChange(c ? [...value, k] : value.filter((x) => x !== k))} data-testid={`${prefix}-format-${k}`} /> {l}
      </label>
    ))}
  </div>
);

export const EmailReportDialog = ({ open, onClose, params, periodLabel }) => {
  const [recipients, setRecipients] = useState([]);
  const [formats, setFormats] = useState(['pdf', 'xlsx']);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/api/visa-costs/email-settings').then(({ data }) => {
      setRecipients([...new Set([data.finance_email, ...(data.recipients || [])].filter(Boolean))]);
      setFormats(data.formats || ['pdf', 'xlsx']);
    }).catch(() => {});
  }, [open]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/visa-costs/report/email', { ...params, recipients, formats, message });
      toast({ title: 'Report sent', description: `Sent to ${data.sent_to.join(', ')}` });
      onClose();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="visa-email-report-dialog">
        <DialogHeader><DialogTitle>Email visa cost report</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Report for <b>{periodLabel}</b> with your current filters. Finance and saved recipients are pre-filled — add or remove as needed.</p>
        <div className="space-y-3">
          <div><Label className="text-xs">Recipients</Label><RecipientInput value={recipients} onChange={setRecipients} testId="vc-email-recipients" /></div>
          <div><Label className="text-xs">Attach</Label><Formats value={formats} onChange={setFormats} prefix="vc-email" /></div>
          <div><Label className="text-xs">Message (optional)</Label><Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="vc-email-message" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || !recipients.length || !formats.length} data-testid="vc-email-send"><Send className="h-4 w-4 mr-1" />{busy ? 'Sending…' : 'Send report'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EmailSettingsDialog = ({ open, onClose, canEdit }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => { if (open) api.get('/api/visa-costs/email-settings').then((r) => setS(r.data)).catch(err); }, [open]);
  const set = (k) => (v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy('save');
    try {
      const { last_monthly_sent, last_annual_sent, updated_at, updated_by, ...payload } = s;
      setS(await api.put('/api/visa-costs/email-settings', payload).then((r) => r.data));
      toast({ title: 'Email settings saved' });
    } catch (e) { err(e); } finally { setBusy(''); }
  };
  const sendNow = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/api/visa-costs/email-settings/send-now?kind=${kind}`);
      toast({ title: kind === 'annual' ? 'Annual forecast sent' : 'Monthly renewal email sent', description: `Sent to ${data.sent_to.join(', ')}` });
    } catch (e) { err(e); } finally { setBusy(''); }
  };

  if (!s) return null;
  const ro = !canEdit;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="visa-email-settings-dialog">
        <DialogHeader><DialogTitle>Visa report email settings</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Finance Team email</Label>
            <Input disabled={ro} value={s.finance_email} onChange={(e) => set('finance_email')(e.target.value)} placeholder="finance@company.com" data-testid="vc-settings-finance-email" /></div>
          <div><Label className="text-xs">Other recipients</Label>
            {ro ? <p className="text-sm">{s.recipients.join(', ') || '—'}</p> : <RecipientInput value={s.recipients} onChange={set('recipients')} testId="vc-settings-recipients" />}</div>
          <div><Label className="text-xs">Attachments</Label><Formats value={s.formats} onChange={set('formats')} disabled={ro} prefix="vc-settings" /></div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between"><p className="text-sm font-medium">Monthly renewals email (next month's visas + budget)</p>
              <Switch disabled={ro} checked={s.monthly_enabled} onCheckedChange={set('monthly_enabled')} data-testid="vc-settings-monthly-enabled" /></div>
            <div className="flex items-center gap-2 text-sm">Day
              <Input disabled={ro} type="number" min={1} max={28} className="h-8 w-16" value={s.monthly_day} onChange={(e) => set('monthly_day')(Number(e.target.value))} data-testid="vc-settings-monthly-day" />
              of each month at <Input disabled={ro} type="time" className="h-8 w-28" value={s.monthly_time} onChange={(e) => set('monthly_time')(e.target.value)} data-testid="vc-settings-monthly-time" /> Dubai</div>
            {canEdit && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => sendNow('monthly')} data-testid="vc-send-monthly-now">{busy === 'monthly' ? 'Sending…' : 'Send next month\'s list now'}</Button>}
          </div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between"><p className="text-sm font-medium">Annual renewal forecast (next calendar year)</p>
              <Switch disabled={ro} checked={s.annual_enabled} onCheckedChange={set('annual_enabled')} data-testid="vc-settings-annual-enabled" /></div>
            <div className="flex items-center gap-2 text-sm flex-wrap">On
              <Input disabled={ro} type="number" min={1} max={31} className="h-8 w-16" value={s.annual_day} onChange={(e) => set('annual_day')(Number(e.target.value))} data-testid="vc-settings-annual-day" />
              <Select disabled={ro} value={String(s.annual_month)} onValueChange={(v) => set('annual_month')(Number(v))}>
                <SelectTrigger className="h-8 w-24" data-testid="vc-settings-annual-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              at <Input disabled={ro} type="time" className="h-8 w-28" value={s.annual_time} onChange={(e) => set('annual_time')(e.target.value)} data-testid="vc-settings-annual-time" /> Dubai</div>
            {canEdit && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => sendNow('annual')} data-testid="vc-send-annual-now">{busy === 'annual' ? 'Sending…' : 'Send next year\'s forecast now'}</Button>}
          </div>
          {(s.last_monthly_sent || s.last_annual_sent) && <p className="text-xs text-gray-500">Last automatic send — monthly: {s.last_monthly_sent || '—'} · annual: {s.last_annual_sent || '—'}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && <Button onClick={save} disabled={!!busy} data-testid="vc-settings-save">{busy === 'save' ? 'Saving…' : 'Save settings'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
